import * as atlas from 'azure-maps-control';
import React, { useContext, useMemo } from 'react';
import { MobileStockLocationSessionContext } from '../../../../../context/MobileStockLocationSessionContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { MobileStockLocationSessionStatus } from '../../../../../types/mobile_stock_location';
import {
  StockLocationMarkerLayer,
  StockLocationClusterLayer,
  StockLocationMarker,
  VentoryMap,
} from '../../../../components/Map';

interface StockLocationSessionMapProps {
  stockLocationIds: Set<string>;
  onMapReady?: (map: atlas.Map) => void;
}

export default function StockLocationSessionMap({ stockLocationIds, onMapReady }: StockLocationSessionMapProps) {
  const { stockLocations } = useContext(StockLocationContext);
  const { sessions, sessionsByStockLocationId } = useContext(MobileStockLocationSessionContext);

  const markerLayer = new StockLocationMarkerLayer();
  const clusterLayer = new StockLocationClusterLayer();

  class StockLocationSessionMarker extends StockLocationMarker {
    get id(): string {
      return this.session?.id ?? this.stockLocation.id;
    }
  }

  markerLayer.markerBuilder = (stockLocation, session) => {
    return new StockLocationSessionMarker(stockLocation, session);
  };

  const markers = useMemo(() => {
    const activeSessions = [...sessions.values()].filter(s => s.status === MobileStockLocationSessionStatus.active);
    const stockLocationIdsWithActiveSessions = new Set(activeSessions.map(s => s.stockLocationId));
    const stockLocationsWithoutActiveSessions = [...stockLocations.values()].filter(
      sl => !stockLocationIdsWithActiveSessions.has(sl.id),
    );

    const arr = [];

    for (const session of activeSessions) {
      if (!stockLocationIds.has(session.stockLocationId)) continue;
      const stockLocation = stockLocations.get(session.stockLocationId);
      if (!stockLocation) continue;
      const marker = markerLayer.markerBuilder(stockLocation, session);
      arr.push(marker);
    }

    for (const stockLocation of stockLocationsWithoutActiveSessions) {
      if (!stockLocationIds.has(stockLocation.id)) continue;
      const session = sessionsByStockLocationId.get(stockLocation.id)?.[0];
      const marker = markerLayer.markerBuilder(stockLocation, session);
      arr.push(marker);
    }

    return new Map(arr.map(m => [m.id, m]));
  }, [stockLocationIds, stockLocations, sessions]);

  return (
    <VentoryMap<StockLocationSessionMarker>
      markerLayer={markerLayer}
      clusterLayer={clusterLayer}
      markers={markers}
      onMapReady={onMapReady}
    />
  );
}
